import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Package, 
  Truck, 
  AlertTriangle, 
  CheckCircle2,
  Search,
  ArrowRight,
  Plus,
  RefreshCcw,
  Edit3,
  ScanBarcode,
  Loader2,
  FileEdit,
  X
} from 'lucide-react';
import { GRNItemRow } from './GRNItemRow';
import { ManualItemsTable } from './GRNManualItems';
import { GRNStepSelectPO } from './GRNStepSelectPO';
import { GRNStepReview } from './GRNStepReview';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function GRNCreateDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Select PO, 2: Enter Items, 3: Review
  const [loading, setLoading] = useState(false);
  const [loadingPOs, setLoadingPOs] = useState(false);
  
  // Manual GRN Mode
  const [isManualMode, setIsManualMode] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [manualSupplierId, setManualSupplierId] = useState('');
  const [manualSupplierName, setManualSupplierName] = useState('');
  
  // PO Selection
  const [availablePOs, setAvailablePOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poSearchTerm, setPOSearchTerm] = useState('');
  
  // GRN Data
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  
  // Disclaimer flags
  const [hasShortage, setHasShortage] = useState(false);
  const [hasAlternateStock, setHasAlternateStock] = useState(false);
  const [hasDamagedStock, setHasDamagedStock] = useState(false);
  const [disclaimerRemarks, setDisclaimerRemarks] = useState('');
  
  // Products for alternate stock selection
  const [products, setProducts] = useState([]);
  
  // User access for MRP update permission
  const [userAccess, setUserAccess] = useState(null);
  
  // MRP updates tracking
  const [mrpUpdates, setMrpUpdates] = useState({});
  
  // Barcode Scanner
  const [scannerMode, setScannerMode] = useState(false);
  const [scannerBarcode, setScannerBarcode] = useState('');
  const [scannerLoading, setScannerLoading] = useState(false);
  const [highlightedItemIndex, setHighlightedItemIndex] = useState(null);
  const scannerInputRef = useRef(null);
  const itemRefs = useRef({});
  
  // Fetch available POs (also used by Refresh button)
  const fetchAvailablePOs = useCallback(async () => {
    setLoadingPOs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API + '/grn/po-list', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setAvailablePOs(response.data);
    } catch (err) {
      toast.error('Failed to fetch Purchase Orders');
    } finally {
      setLoadingPOs(false);
    }
  }, []);

  // Reset and fetch data on open
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedPO(null);
    setIsManualMode(false);
    setManualSupplierId('');
    setManualSupplierName('');
    setItems([]);
    setNotes('');
    setHasShortage(false);
    setHasAlternateStock(false);
    setHasDamagedStock(false);
    setDisclaimerRemarks('');
    setReceivedDate(new Date().toISOString().split('T')[0]);
    setMrpUpdates({});
    setScannerMode(false);
    setScannerBarcode('');
    setHighlightedItemIndex(null);
    fetchAvailablePOs();
    const token = localStorage.getItem('token');
    const headers = { Authorization: 'Bearer ' + token };
    axios.get(API + '/products', { headers }).then(res => setProducts(res.data)).catch(() => {});
    axios.get(API + '/grn/user-access', { headers }).then(res => setUserAccess(res.data)).catch(() => {});
    axios.get(API + '/suppliers', { headers }).then(res => {
      setSuppliers(res.data);
    }).catch(() => {});
    // Intentional reset-on-open effect; state setters / module imports are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchAvailablePOs]);
  
  // Barcode Scanner Functions
  async function handleBarcodeScan() {
    if (!scannerBarcode.trim() || scannerLoading) return;
    
    setScannerLoading(true);
    const cleanBarcode = scannerBarcode.trim();
    
    try {
      const token = localStorage.getItem('token');
      
      // Look up product by barcode
      const response = await axios.get(API + '/products/barcode/' + encodeURIComponent(cleanBarcode), {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      const scannedProduct = response.data;
      
      // Find this product in GRN items
      const itemIndex = items.findIndex(item => 
        item.product_id === scannedProduct.id || 
        products.find(p => p.id === item.product_id)?.barcode === cleanBarcode
      );
      
      if (itemIndex >= 0) {
        // Product found in GRN items - highlight it and scroll to it
        setHighlightedItemIndex(itemIndex);
        playBeep(true);
        toast.success(`Found: ${scannedProduct.name}`, {
          description: `Item #${itemIndex + 1} - Enter received quantity`
        });
        
        // Scroll to the item
        if (itemRefs.current[itemIndex]) {
          itemRefs.current[itemIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus on received quantity input after a short delay
          setTimeout(() => {
            const input = itemRefs.current[itemIndex]?.querySelector('input[data-field="received_quantity"]');
            if (input) input.focus();
          }, 300);
        }
        
        // Clear highlight after 3 seconds
        setTimeout(() => setHighlightedItemIndex(null), 3000);
      } else {
        // Product not in this PO
        playBeep(false);
        toast.error(`Product not in this PO`, {
          description: `${scannedProduct.name} (${cleanBarcode}) is not part of this Purchase Order`
        });
      }
      
      setScannerBarcode('');
      
    } catch (err) {
      playBeep(false);
      toast.error('Product not found', {
        description: `No product with barcode: ${cleanBarcode}`
      });
    } finally {
      setScannerLoading(false);
      // Refocus scanner input
      if (scannerInputRef.current) {
        scannerInputRef.current.focus();
      }
    }
  }
  
  function handleScannerKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeScan();
    }
  }
  
  function playBeep(success) {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = success ? 800 : 300;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + (success ? 0.1 : 0.3));
    } catch (audioErr) {
      // Web Audio API unavailable (older browser / blocked context). Safe to ignore — barcode flow continues.
      // Logged at debug level only so we don't noise up production logs for an optional UX nicety.
      console.debug('Barcode audio cue skipped:', audioErr?.message || audioErr);
    }
  }
  
  function handleSelectPO(po) {
    setSelectedPO(po);
    
    // Initialize items from PO with pending quantities and MRP
    // pending_quantities comes from the API and shows what's still pending after previous GRNs
    const grnItems = po.items.map(item => {
      const totalOrdered = item.quantity || 0;
      const pendingQty = po.pending_quantities?.[item.product_id] ?? item.quantity;
      const prevReceived = totalOrdered - pendingQty;
      // Find product to get current MRP and barcode
      const product = products.find(p => p.id === item.product_id);
      const currentMRP = product?.mrp || item.mrp || 0;
      
      return {
        product_id: item.product_id,
        product_name: item.product_name,
        product_barcode: product?.barcode || '',
        po_rate: item.purchase_rate ?? 0,         // rate the PO was raised at
        catalogue_rate: product?.inward_b2b_price ?? product?.purchase_price ?? 0,  // current Master rate
        total_ordered: totalOrdered,          // Total ordered in PO
        previously_received: prevReceived,     // From previous GRNs
        pending_qty: pendingQty,               // What's still pending
        ordered_quantity: pendingQty,          // For this GRN (max = pending)
        received_quantity: 0,                  // Default to 0 — user must enter manually
        shortage_quantity: pendingQty,
        damaged_quantity: 0,
        accepted_quantity: 0,
        batch_number: '',
        expiry_date: '',
        is_alternate: false,
        original_product_id: null,
        alternate_remarks: '',
        current_mrp: currentMRP,
        new_mrp: currentMRP,
      };
    });
    
    setItems(grnItems);
    setMrpUpdates({});
    setStep(2);
  }
  
  // Start Manual GRN Mode
  function startManualMode() {
    setIsManualMode(true);
    setSelectedPO(null);
    setItems([]);
    setStep(2);
  }
  
  // Add item in manual mode
  function addManualItem() {
    const newItem = {
      product_id: '',
      product_name: '',
      product_barcode: '',
      total_ordered: 0,
      previously_received: 0,
      pending_qty: 0,
      ordered_quantity: 0,
      received_quantity: 0,
      shortage_quantity: 0,
      damaged_quantity: 0,
      accepted_quantity: 0,
      batch_number: '',
      expiry_date: '',
      is_alternate: false,
      original_product_id: null,
      alternate_remarks: '',
      current_mrp: 0,
      new_mrp: 0,
      is_manual: true,
    };
    setItems([...items, newItem]);
  }
  
  // Update manual item product selection
  function updateManualItemProduct(index, productId) {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      product_id: productId,
      product_name: product.name,
      product_barcode: product.barcode || '',
      current_mrp: product.mrp || 0,
      new_mrp: product.mrp || 0,
    };
    setItems(newItems);
  }
  
  // Remove manual item
  function removeManualItem(index) {
    setItems(items.filter((_, i) => i !== index));
  }
  
  // Calculate derived values when items change
  const updateItemCalculations = useCallback((index, field, value) => {
    const newItems = [...items];
    const item = { ...newItems[index] };
    
    if (field === 'received_quantity') {
      item.received_quantity = parseFloat(value) || 0;
      // Auto-calculate shortage if received < ordered
      if (item.received_quantity < item.ordered_quantity) {
        item.shortage_quantity = item.ordered_quantity - item.received_quantity;
      } else {
        item.shortage_quantity = 0;
      }
    } else if (field === 'damaged_quantity') {
      item.damaged_quantity = parseFloat(value) || 0;
    } else if (field === 'shortage_quantity') {
      item.shortage_quantity = parseFloat(value) || 0;
    } else if (field === 'batch_number') {
      item.batch_number = value;
    } else if (field === 'expiry_date') {
      item.expiry_date = value;
    } else if (field === 'new_mrp') {
      item.new_mrp = parseFloat(value) || 0;
      // Track MRP update
      if (item.new_mrp !== item.current_mrp && item.product_id) {
        setMrpUpdates(prev => ({
          ...prev,
          [item.product_id]: item.new_mrp
        }));
      } else {
        setMrpUpdates(prev => {
          const updated = { ...prev };
          delete updated[item.product_id];
          return updated;
        });
      }
    }
    
    // Calculate accepted quantity: received - damaged
    item.accepted_quantity = Math.max(0, item.received_quantity - item.damaged_quantity);
    
    newItems[index] = item;
    setItems(newItems);
    
    // Auto-detect flags
    const anyShortage = newItems.some(i => i.shortage_quantity > 0);
    const anyDamaged = newItems.some(i => i.damaged_quantity > 0);
    const anyAlternate = newItems.some(i => i.is_alternate);
    
    setHasShortage(anyShortage);
    setHasDamagedStock(anyDamaged);
    setHasAlternateStock(anyAlternate);
    
    // State setters are stable; only items drives the recomputation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  
  function addAlternateItem() {
    const newItem = {
      product_id: '',
      product_name: '',
      ordered_quantity: 0,
      received_quantity: 0,
      shortage_quantity: 0,
      damaged_quantity: 0,
      accepted_quantity: 0,
      batch_number: '',
      expiry_date: '',
      is_alternate: true,
      original_product_id: items[0]?.product_id || '',
      alternate_remarks: '',
      current_mrp: 0,
      new_mrp: 0,
    };
    setItems([...items, newItem]);
    setHasAlternateStock(true);
  }
  
  function removeItem(index) {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    
    // Recalculate flags
    const anyAlternate = newItems.some(i => i.is_alternate);
    setHasAlternateStock(anyAlternate);
  }
  
  function updateAlternateItem(index, field, value) {
    const newItems = [...items];
    const item = { ...newItems[index] };
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      item.product_id = value;
      item.product_name = product?.name || '';
      item.current_mrp = product?.mrp || 0;
      item.new_mrp = product?.mrp || 0;
    } else if (field === 'received_quantity') {
      item.received_quantity = parseFloat(value) || 0;
      item.accepted_quantity = item.received_quantity - item.damaged_quantity;
    } else if (field === 'damaged_quantity') {
      item.damaged_quantity = parseFloat(value) || 0;
      item.accepted_quantity = item.received_quantity - item.damaged_quantity;
    } else if (field === 'alternate_remarks') {
      item.alternate_remarks = value;
    } else if (field === 'new_mrp') {
      item.new_mrp = parseFloat(value) || 0;
      if (item.new_mrp !== item.current_mrp && item.product_id) {
        setMrpUpdates(prev => ({
          ...prev,
          [item.product_id]: item.new_mrp
        }));
      }
    } else {
      item[field] = value;
    }
    
    newItems[index] = item;
    setItems(newItems);
  }
  
  async function handleSubmit() {
    // Validate
    if (items.length === 0) {
      toast.error('No items to receive');
      return;
    }
    
    // Validate manual mode
    if (isManualMode) {
      if (!manualSupplierId) {
        toast.error('Please select a supplier for manual GRN');
        return;
      }
      const invalidItems = items.filter(i => !i.product_id || i.received_quantity <= 0);
      if (invalidItems.length > 0) {
        toast.error('Please select product and enter quantity for all items');
        return;
      }
    }
    
    // Check if disclaimer is required
    const needsDisclaimer = hasShortage || hasAlternateStock || hasDamagedStock;
    if (needsDisclaimer && !disclaimerRemarks.trim()) {
      toast.error('Disclaimer remarks are required when there is shortage, alternate stock, or damaged items');
      return;
    }
    
    // Validate alternate items have product selected
    const invalidAlternate = items.filter(i => i.is_alternate && !i.product_id);
    if (invalidAlternate.length > 0) {
      toast.error('Please select products for all alternate items');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build MRP updates array
      const mrpUpdatesList = Object.keys(mrpUpdates).map(product_id => ({
        product_id,
        new_mrp: mrpUpdates[product_id]
      }));
      
      const itemsPayload = items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        ordered_quantity: item.ordered_quantity || item.received_quantity,
        received_quantity: item.received_quantity,
        shortage_quantity: item.shortage_quantity,
        damaged_quantity: item.damaged_quantity,
        accepted_quantity: item.accepted_quantity,
        batch_number: item.batch_number || null,
        expiry_date: item.expiry_date || null,
        is_alternate: item.is_alternate,
        original_product_id: item.original_product_id,
        alternate_remarks: item.alternate_remarks || null,
      }));
      
      if (isManualMode) {
        // Manual GRN - create without PO
        const payload = {
          supplier_id: manualSupplierId,
          supplier_name: manualSupplierName,
          received_date: receivedDate,
          items: itemsPayload,
          notes: notes || null,
          has_shortage: hasShortage,
          has_alternate_stock: hasAlternateStock,
          has_damaged_stock: hasDamagedStock,
          disclaimer_remarks: disclaimerRemarks || null,
          mrp_updates: mrpUpdatesList.length > 0 ? mrpUpdatesList : null,
          is_manual: true,
        };
        
        await axios.post(API + '/grn/manual', payload, {
          headers: { Authorization: 'Bearer ' + token }
        });
      } else {
        // PO-linked GRN
        const payload = {
          purchase_order_id: selectedPO.id,
          received_date: receivedDate,
          items: itemsPayload,
          notes: notes || null,
          has_shortage: hasShortage,
          has_alternate_stock: hasAlternateStock,
          has_damaged_stock: hasDamagedStock,
          disclaimer_remarks: disclaimerRemarks || null,
          mrp_updates: mrpUpdatesList.length > 0 ? mrpUpdatesList : null,
        };
        
        await axios.post(API + '/grn/with-mrp-update', payload, {
          headers: { Authorization: 'Bearer ' + token }
        });
      }
      
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create GRN');
    } finally {
      setLoading(false);
    }
  }
  
  // Filter POs by search
  const filteredPOs = availablePOs.filter(po => 
    po.po_number?.toLowerCase().includes(poSearchTerm.toLowerCase()) ||
    po.supplier_name?.toLowerCase().includes(poSearchTerm.toLowerCase())
  );
  
  // Calculate totals
  const totalOrdered = items.reduce((sum, i) => sum + (i.ordered_quantity || 0), 0);
  const totalReceived = items.reduce((sum, i) => sum + (i.received_quantity || 0), 0);
  const totalAccepted = items.reduce((sum, i) => sum + (i.accepted_quantity || 0), 0);
  const totalShortage = items.reduce((sum, i) => sum + (i.shortage_quantity || 0), 0);
  const totalDamaged = items.reduce((sum, i) => sum + (i.damaged_quantity || 0), 0);
  const mrpUpdatesCount = Object.keys(mrpUpdates).length;

  // Memoised list of items whose MRP is actually changing — rendered in the
  // summary panel; avoids re-filtering the whole items[] on every keystroke.
  const mrpChangedItems = useMemo(
    () => items.filter(i => i.new_mrp !== i.current_mrp),
    [items],
  );

  // Items where the PO rate differs from Master Catalogue inward rate by >10%.
  // Surfaced as a banner so GRN staff don't miss the per-row amber badge.
  const priceAlertItems = useMemo(
    () => items.filter(i => {
      const po = Number(i.po_rate) || 0;
      const cat = Number(i.catalogue_rate) || 0;
      if (!po || !cat) return false;
      return Math.abs(po - cat) / cat > 0.10;
    }),
    [items],
  );
  const canUpdateMrp = userAccess?.can_update_mrp || false;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <Truck className="w-5 h-5 text-blue-600" />
            Create Goods Receipt Note (GRN)
            {selectedPO && (
              <Badge className="bg-blue-100 text-blue-800">
                {selectedPO.po_number}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 py-4 border-b flex-shrink-0">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</div>
            <span className="text-sm font-medium">Select PO</span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</div>
            <span className="text-sm font-medium">Enter Quantities</span>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300" />
          <div className={`flex items-center gap-2 ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3</div>
            <span className="text-sm font-medium">Review & Submit</span>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto py-4">
          {/* Step 1: Select PO */}
          {step === 1 && (
            <GRNStepSelectPO
              filteredPOs={filteredPOs}
              loadingPOs={loadingPOs}
              poSearchTerm={poSearchTerm}
              setPOSearchTerm={setPOSearchTerm}
              onSelectPO={handleSelectPO}
              onStartManual={startManualMode}
              onRefreshPOs={fetchAvailablePOs}
            />
          )}
          
          {/* Step 2: Enter Quantities */}
          {step === 2 && (selectedPO || isManualMode) && (
            <div className="space-y-4">
              {/* PO Info or Manual Mode Header */}
              {isManualMode ? (
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <FileEdit className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="font-semibold text-orange-800">Manual GRN Entry</p>
                        <p className="text-sm text-orange-600">Direct stock receipt without Purchase Order</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="min-w-[200px]">
                        <Label className="text-xs text-gray-500">Supplier *</Label>
                        <Select 
                          value={manualSupplierId} 
                          onValueChange={(val) => {
                            setManualSupplierId(val);
                            const supplier = suppliers.find(s => s.id === val);
                            setManualSupplierName(supplier?.name || '');
                          }}
                        >
                          <SelectTrigger className="h-8" data-testid="manual-supplier-select">
                            <SelectValue placeholder="Select supplier..." />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Received Date</Label>
                        <Input
                          type="date"
                          value={receivedDate}
                          onChange={(e) => setReceivedDate(e.target.value)}
                          className="h-8 w-36"
                          data-testid="received-date"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-blue-800">{selectedPO.po_number}</p>
                      <p className="text-sm text-blue-600">Supplier: {selectedPO.supplier_name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Barcode Scanner Toggle */}
                      <Button
                        type="button"
                        variant={scannerMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setScannerMode(!scannerMode);
                          if (!scannerMode) {
                            setTimeout(() => scannerInputRef.current?.focus(), 100);
                          }
                        }}
                        className={`gap-2 ${scannerMode ? 'bg-green-600 hover:bg-green-700' : ''}`}
                        data-testid="grn-scanner-toggle"
                      >
                        <ScanBarcode className="w-4 h-4" />
                        {scannerMode ? 'Scanner ON' : 'Scanner'}
                      </Button>
                      <div>
                        <Label className="text-xs text-gray-500">Received Date</Label>
                        <Input
                          type="date"
                          value={receivedDate}
                          onChange={(e) => setReceivedDate(e.target.value)}
                          className="h-8 w-36"
                          data-testid="received-date"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Barcode Scanner Section - only for PO mode */}
              {!isManualMode && scannerMode && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <ScanBarcode className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Barcode Scanner Mode</span>
                    <Badge className="bg-green-600">Active</Badge>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <ScanBarcode className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
                      <Input
                        ref={scannerInputRef}
                        type="text"
                        value={scannerBarcode}
                        onChange={(e) => setScannerBarcode(e.target.value)}
                        onKeyDown={handleScannerKeyDown}
                        placeholder="Scan barcode to find item in PO..."
                        className="pl-10 font-mono border-green-300 focus:ring-green-500"
                        disabled={scannerLoading}
                        data-testid="grn-barcode-input"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleBarcodeScan}
                      disabled={scannerLoading || !scannerBarcode.trim()}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {scannerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find'}
                    </Button>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    Scan product barcode to quickly locate and enter received quantity
                  </p>
                </div>
              )}
              
              {/* MRP Update Notice */}
              {canUpdateMrp && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-800">
                    <Edit3 className="w-4 h-4" />
                    <span className="font-medium text-sm">MRP Correction Enabled</span>
                    <span className="text-xs text-green-600">- You can update product MRP during GRN creation</span>
                  </div>
                </div>
              )}

              {/* Price-change alert — when the PO rate differs from the
                  Master Catalogue inward rate by more than 10% on any line. */}
              {priceAlertItems.length > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-300" data-testid="price-change-banner">
                  <div className="flex items-start gap-2 text-amber-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium">
                        {priceAlertItems.length} item(s) have a purchase-rate change &gt; 10% vs Master Catalogue
                      </p>
                      <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">
                        {priceAlertItems.slice(0, 5).map((i, k) => {
                          const po = Number(i.po_rate) || 0;
                          const cat = Number(i.catalogue_rate) || 0;
                          const pct = cat ? ((po - cat) / cat) * 100 : 0;
                          return (
                            <li key={`${i.product_id}-${k}`}>
                              <strong>{i.product_name}</strong>: PO ₹{po.toFixed(2)} vs Master ₹{cat.toFixed(2)} ({pct > 0 ? '+' : ''}{pct.toFixed(1)}%)
                            </li>
                          );
                        })}
                        {priceAlertItems.length > 5 && (
                          <li className="text-amber-700">…and {priceAlertItems.length - 5} more</li>
                        )}
                      </ul>
                      <p className="text-xs text-amber-700 mt-1">
                        Verify rates with procurement before finalising the GRN — Master Catalogue will be overwritten on completion.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Manual Mode: Add Item Section */}
              {isManualMode && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addManualItem}
                    className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="add-manual-item-btn"
                  >
                    <Plus size={16} />
                    Add Item
                  </Button>
                </div>
              )}
              
              {/* Items Table */}
              {isManualMode ? (
                <ManualItemsTable
                  items={items}
                  products={products}
                  canUpdateMrp={canUpdateMrp}
                  onUpdateProduct={updateManualItemProduct}
                  onUpdateItem={updateItemCalculations}
                  onRemove={removeManualItem}
                />
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Product</th>
                        <th className="px-2 py-2 text-center font-semibold w-16 bg-blue-50">Ordered</th>
                        <th className="px-2 py-2 text-center font-semibold w-16 bg-green-50">Prev Rcvd</th>
                        <th className="px-2 py-2 text-center font-semibold w-16 bg-orange-50">Pending</th>
                        <th className="px-2 py-2 text-center font-semibold w-24 bg-yellow-50">Receive Now</th>
                        <th className="px-2 py-2 text-center font-semibold w-20">Shortage</th>
                        <th className="px-2 py-2 text-center font-semibold w-20">Damaged</th>
                        <th className="px-2 py-2 text-center font-semibold w-20 bg-green-100">Accepted</th>
                        <th className="px-2 py-2 text-center font-semibold w-20">MRP ₹</th>
                        {canUpdateMrp && (
                          <th className="px-2 py-2 text-center font-semibold w-24 bg-green-50">Update MRP</th>
                        )}
                        <th className="px-2 py-2 text-center font-semibold w-24">Batch #</th>
                        <th className="px-2 py-2 text-center font-semibold w-24">Expiry</th>
                        <th className="px-2 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {items.map((item, idx) => (
                        <GRNItemRow
                          key={`${item.product_id}-${idx}`}
                          item={item}
                          idx={idx}
                          products={products}
                          canUpdateMrp={canUpdateMrp}
                          highlighted={highlightedItemIndex === idx}
                          onUpdateItem={updateItemCalculations}
                          onUpdateAlternate={updateAlternateItem}
                          onRemove={removeItem}
                          itemRef={(el) => itemRefs.current[idx] = el}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Add Alternate Button - only for PO mode */}
              {!isManualMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addAlternateItem}
                  className="gap-2"
                  data-testid="add-alternate-btn"
                >
                  <Plus size={16} />
                  Add Alternate Product
                </Button>
              )}
              
              {/* Summary */}
              <div className="grid grid-cols-6 gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Ordered</p>
                  <p className="font-semibold">{totalOrdered}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Received</p>
                  <p className="font-semibold text-blue-600">{totalReceived}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Shortage</p>
                  <p className={`font-semibold ${totalShortage > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                    {totalShortage}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Damaged</p>
                  <p className={`font-semibold ${totalDamaged > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {totalDamaged}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Accepted</p>
                  <p className="font-bold text-green-600">{totalAccepted}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">MRP Updates</p>
                  <p className={`font-semibold ${mrpUpdatesCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {mrpUpdatesCount}
                  </p>
                </div>
              </div>
              
              {/* MRP Updates Preview */}
              {canUpdateMrp && mrpUpdatesCount > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 mb-2">
                    <Edit3 size={16} />
                    <span className="font-semibold text-sm">MRP Updates to Apply ({mrpUpdatesCount})</span>
                  </div>
                  <div className="text-sm text-green-700">
                    {mrpChangedItems.map((item, idx) => (
                      <p key={`mrp-${item.product_id}-${idx}`}>
                        • {item.product_name}: ₹{item.current_mrp} → <strong>₹{item.new_mrp}</strong>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Disclaimer Section */}
              {(hasShortage || hasAlternateStock || hasDamagedStock) && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <AlertTriangle size={18} />
                    <span className="font-semibold">Disclaimer Required</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    {hasShortage && (
                      <Badge className="bg-yellow-100 text-yellow-800">Shortage Received</Badge>
                    )}
                    {hasAlternateStock && (
                      <Badge className="bg-blue-100 text-blue-800">Alternate Stock Supplied</Badge>
                    )}
                    {hasDamagedStock && (
                      <Badge className="bg-red-100 text-red-800">Damaged Stock Recorded</Badge>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-yellow-800">Remarks / Disclaimer Notes *</Label>
                    <Textarea
                      value={disclaimerRemarks}
                      onChange={(e) => setDisclaimerRemarks(e.target.value)}
                      placeholder="Explain the shortage, alternate stock, or damaged items..."
                      className="mt-1 bg-white"
                      rows={3}
                      data-testid="disclaimer-remarks"
                    />
                  </div>
                </div>
              )}
              
              {/* General Notes */}
              <div>
                <Label>Additional Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional remarks about this GRN..."
                  className="mt-1"
                  rows={2}
                  data-testid="grn-notes"
                />
              </div>
            </div>
          )}
          
          {/* Step 3: Review */}
          {step === 3 && selectedPO && (
            <GRNStepReview
              selectedPO={selectedPO}
              receivedDate={receivedDate}
              items={items}
              totalOrdered={totalOrdered}
              totalReceived={totalReceived}
              totalAccepted={totalAccepted}
              disclaimerRemarks={disclaimerRemarks}
              notes={notes}
            />
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-between pt-4 border-t">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="prev-step-btn">
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-grn-btn">
              Cancel
            </Button>
            {step === 2 && (
              <Button onClick={() => setStep(3)} data-testid="next-step-btn">
                Review
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={handleSubmit} disabled={loading} data-testid="submit-grn-btn">
                <CheckCircle2 className="mr-2 w-4 h-4" />
                {loading ? 'Creating...' : 'Confirm & Create GRN'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GRNCreateDialog;
